import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Calendar } from 'react-multi-date-picker';
import moment from 'moment';
import { useIntl } from 'react-intl';
import Dropdown from '../../base/Dropdown/Dropdown';
import TextInput from '../TextInput/TextInput';
import gregorian_ru from '../RangeDatePicker/gregorian_ru';
import { Icons } from '../../base/BootstrapComponents';
import '../RangeDatePicker/RangeDatePickerStyles.scss';
import CheckBoxInputWithLabel from '../CheckBoxInput/CheckBoxInputWithLabel';
import { addZero } from '../../../utils/utils';
import './DatePickerStyles.scss';

const defaultDateFormat = 'DD.MM.YYYY';

export default function DateTimePicker(props) {
  const {
    className = '',
    disabled = false,
    inputProps = {},
    withLabel = false,
    usePortal = true,
    inline = false,
    fieldState = {
      error: '',
      touched: false,
    },
    disableInitialSelect = false,
    notDefaultValue = false,
    withFormat = null,
    timeFormat = null,
    variant = null,
    label = '',
    hideSeconds = false,
    hideTime = false,
    changeFormat = null,
    onChangeToIso = false,
  } = props;
  const controlId = `date-time-picker-${Math.random() * 10000}`;
  const { onChange = () => {}, name, value = '' } = inputProps;
  const { error = '', touched = false } = fieldState;
  const pickerRef = useRef(null);
  const anchorElement = useRef(null);
  const [currentValue, setCurrent] = useState(value);
  const [pickerKey, setPickerKey] = useState(0);
  const [showTimeSelect, setShowTimeSelect] = useState(false);
  const [hourIndex, setHourIndex] = useState(0);
  const [minIndex, setMinIndex] = useState(0);
  const [secIndex, setSecIndex] = useState(0);
  const [startPos, setStartPos] = useState(0);
  const [dragElem, setDragElem] = useState('');
  const [dateValue, setDateValue] = useState('');
  const [timeValue, setTimeValue] = useState('');
  const [disableTime, setDisableTime] = useState(false);
  const blockHeight = 32;
  const format = withFormat || defaultDateFormat;
  const selectedTimeFormat = timeFormat || 'HH:mm:ss';
  const self = this;
  const { formatMessage } = useIntl();
  const tz = window.localStorage.getItem('USER_TIME_ZONE') || moment.tz.guess();

  useEffect(() => {
    let date = value || '';
    if (value && typeof value === 'string') {
      date = moment.tz(value, tz).toISOString();
    }
    setCurrent(date);
    window.addEventListener('mouseup', onMouseUp, controlId, self);
    return () => {
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    let date = value || '';
    if (value && typeof value === 'string') {
      date = moment.tz(value, tz).toISOString();
    }
    setCurrent(date);
  }, [value]);

  const handleShowTimeSelector = () => {
    setShowTimeSelect(true);
  };

  const onSelect = (value, index = 0) => {
    if (!inputProps) {
      return;
    }
    if (disableInitialSelect || notDefaultValue) {
      return;
    }
    let mv = moment.tz(value, tz);
    mv = mv && mv.isValid ? mv : moment().tz(tz);
    setCurrent(mv.toISOString());

    if (changeFormat) {
      onChange({ target: { value: mv.format(changeFormat), name } });
    } else if (onChangeToIso) {
      onChange({ target: { value: mv.toISOString(), name } });
    } else {
      onChange({ target: { value: mv.format('YYYY-MM-DDTHH:mm:ss'), name } });
    }
    setTimeout(() => {
      setPickerKey(pickerKey + 1);
    }, 50);
  };

  const onChangeDate = date => {
    onSelect(date.toDate());
  };

  const handleSelectHour = (v, index) => () => {
    let newV = v;
    if (v > 23) {
      newV = v - 24;
    }
    if (v < 0) {
      newV = 24 + v;
    }
    onSelect(moment(currentValue).clone().set('hour', newV).toDate());
    setHourIndex(index);
  };

  const handleSelectMinute = (v, index) => () => {
    let newV = v;
    if (v > 59) {
      newV = v - 60;
    }
    if (v < 0) {
      newV = 60 + v;
    }
    onSelect(moment(currentValue).clone().set('minute', newV).toDate());
    setMinIndex(index);
  };

  const handleSelectSecond = (v, index) => () => {
    let newV = v;
    if (v > 59) {
      newV = v - 60;
    }
    if (v < 0) {
      newV = 60 + v;
    }
    onSelect(moment(currentValue).set('second', newV).toDate());
    setSecIndex(index);
  };

  const onWheelHour = event => {
    if (!currentValue) return null;
    const delta = event.deltaY > 0 ? 1 : -1;
    handleSelectHour(moment(currentValue).hour() + delta, hourIndex + delta)();
  };

  const handleChangeDateTime = e => {
    setDateValue(e.target.value);
  };

  const onKeyDown = e => {
    if (e.key === 'Enter') {
      handleBlurDateTime();
      handleBlurTime();
    }
  };

  const handleBlurDateTime = () => {
    if (!dateValue) return;
    const newValue = moment(dateValue, !hideTime ? `${format} ${selectedTimeFormat}` : format);
    if (newValue.isValid()) {
      onSelect(newValue.toDate());
    }
    setDateValue('');
  };

  const handleChangeTime = e => {
    setTimeValue(e.target.value);
  };

  const handleBlurTime = () => {
    if (!timeValue) return;
    const parts = timeValue.split(':').map(a => Number(a)).filter(f => !Number.isNaN(f));
    if (parts.length >= 2) {
      let newH = parts[0];
      if (newH > 23) { newH -= 24; }
      if (newH < 0) { newH = 24 + newH; }

      let newM = parts[1];
      if (newM > 59) { newM -= 60; }
      if (newM < 0) { newM = 60 + newM; }

      let newS = parts.length >= 3 ? parts[2] : 0;
      if (newS > 59) { newS -= 60; }
      if (newS < 0) { newS = 60 + newS; }

      onSelect(moment(currentValue)
        .clone()
        .set('hour', newH)
        .set('minute', newM)
        .set('second', newS)
        .toDate());
    }
    setTimeValue('');
  };

  const onWheelMinute = event => {
    if (!currentValue) return null;
    const delta = event.deltaY > 0 ? 1 : -1;
    handleSelectMinute(moment(currentValue).minutes() + delta, minIndex + delta)();
  };

  const onWheelSecond = event => {
    if (!currentValue) return null;
    const delta = event.deltaY > 0 ? 1 : -1;
    handleSelectSecond(moment(currentValue).second() + delta, secIndex + delta)();
  };

  const onDragStart = slide => e => {
    const currentTargetRect = e.currentTarget.getBoundingClientRect();
    setStartPos(currentTargetRect.y);
    setDragElem(slide);
  };

  const onMouseMove = e => {
    if (!startPos) return;
    const y = e.clientY - startPos;
    if (Math.abs(y) > 32) {
      setStartPos(e.clientY);
      if (dragElem === 'hour') { onWheelHour({ deltaY: -1 * y }); }
      if (dragElem === 'min') { onWheelMinute({ deltaY: -1 * y }); }
      if (dragElem === 'sec') { onWheelSecond({ deltaY: -1 * y }); }
    }
  };

  const onChangeDisabledTime = () => {
    setDisableTime(!disableTime);
    let newH = 0;
    let newM = 0;
    let newS = 0;
    if (disableTime) {
      const v = moment();
      newH = v.get('hours');
      newM = v.get('minute');
      newS = v.get('second');
    }
    onSelect(moment(currentValue)
      .clone()
      .set('hour', newH)
      .set('minute', newM)
      .set('second', newS)
      .toDate());
  };

  const onMouseUp = () => {
    setStartPos(0);
    setDragElem('');
  };

  const renderTimeScroller = () => {
    const cv = currentValue ? moment(currentValue) : moment();
    const startVH = cv.clone().subtract(7, 'hours');
    const startVM = cv.clone().subtract(7, 'minute');
    const startVS = cv.clone().subtract(7, 'second');
    const arrayH = [];
    const arrayM = [];
    const arrayS = [];
    for (let i = 0; i < 14; i++) {
      const v = startVH.add(1, 'hours');
      arrayH.push({ value: v.get('hours'), index: hourIndex - 7 + i });
    }
    for (let i = 0; i < 14; i++) {
      const v = startVM.add(1, 'minute');
      arrayM.push({ value: v.get('minute'), index: minIndex - 7 + i });
    }
    for (let i = 0; i < 14; i++) {
      const v = startVS.add(1, 'second');
      arrayS.push({ value: v.get('second'), index: secIndex - 7 + i });
    }
    return (
      <div className="dtp-time-scroller" onMouseMove={onMouseMove}>
        <div className="dtp-time-slider-back" />
        <div className="dtp-time-slider-shadow" />
        <div className="dtp-time-slider is-hour">
          {renderInfinityTimeBlock(arrayH, onWheelHour, handleSelectHour, onDragStart('hour'))}
        </div>
        <div className="dtp-time-slider is-minute">
          {renderInfinityTimeBlock(arrayM, onWheelMinute, handleSelectMinute, onDragStart('min'))}
        </div>
        {!hideSeconds ? (
          <div className="dtp-time-slider is-second">
            {renderInfinityTimeBlock(arrayS, onWheelSecond, handleSelectSecond, onDragStart('sec'))}
          </div>
        ) : null}

      </div>
    );
  };

  const renderInfinityTimeBlock = (list = [], onWheel = () => {}, onSelect = () => {}, onMouseDown = () => {}) => {
    if (!list || !list.length) return null;
    return (
      <div
        className="time-block custom-scroll-theme"
        onWheel={onWheel}
        style={{ transform: `translate(0px, ${-1 * list[6].index * blockHeight}px) translateZ(0px)` }}
      >
        {/* eslint-disable-next-line no-unused-vars */}
        {list.map(({ value, index }) => {
          const pos = index * blockHeight;
          if (Number.isNaN(pos)) return null;
          return (
            <div
              key={index}
              className={classNames('time-item')}
              onClick={onSelect(value, index)}
              style={{ transform: `translate(0px, ${pos}px) translateZ(0px)` }}
              onMouseDown={onMouseDown}
            >
              {addZero(2, value)}
            </div>
          );
        })}
      </div>
    );
  };

  const onReset = event => {
    setDateValue('');
    onChange({ target: { value: '', name } });
    event.stopPropagation();
  };

  return (
    <div className={classNames('form-control-wrapper date-picker', { isDisabled: disabled }, { 'inline-picker': inline }, className)}>
      <div className="custom-react-date-picker">
        <Dropdown
          usePortal={usePortal}
          // setCloseFunction
          closeCallback={() => { handleBlurDateTime(); handleBlurTime(); setShowTimeSelect(false); }}
          ignoreScroll
        >
          <Dropdown.Toggle className="mini-profile-button">
            <div ref={anchorElement} className="date-selector-wrapper">
              <div className="date-selector">
                <TextInput
                  {...props}
                  className={classNames('data-picker-text', { 'is-invalid': error && touched })}
                  containerRef={pickerRef}
                  inputProps={{
                    value: dateValue,
                    onChange: handleChangeDateTime,
                    onBlur: handleBlurDateTime,
                    onKeyDown,
                  }}
                  placeholder={currentValue ? moment.tz(currentValue, tz).format(!hideTime ? `${format} ${selectedTimeFormat}` : format) : ''}
                  withLabel={withLabel}
                  inputAfter={(
                    <div className="search-wrapper">
                      <Icons customIcon className="search" width={21} height={24} icon="calendar" />
                      <Icons customIcon onClick={onReset} width={24} height={24} icon="close" />
                    </div>
                  )}
                  variant={variant}
                />
              </div>
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu
            anchorElement={anchorElement}
            disableClickToggle
            className="data-picker-wrapper dtp-wrapper"
            useResizeContent={false}
          >
            <Calendar
              key={pickerKey}
              locale={gregorian_ru}
              weekStartDayIndex={1}
              onChange={onChangeDate}
              value={currentValue ? new Date(currentValue) : ''}
            />
            {!hideTime ? (
              <div className="dtp-time-wrapper">
                <div className={classNames('dtp-time-selector', { show: showTimeSelect })}>
                  <div className="dtp-time-header">
                    <div className="dtp-time-header-text">{label || formatMessage({ id: 'time' })}</div>
                    <div className="dtp-time-header-button">
                      <div className="dtp-close" onClick={() => setShowTimeSelect(false)}>{formatMessage({ id: 'collapse' })}</div>
                    </div>
                  </div>
                  {renderTimeScroller()}
                </div>
                <div className="dtp-time-input-label">
                  <div className="dtp-time-input-label-text">{formatMessage({ id: 'time' })}</div>
                  <div className="dtp-time-input-label-checkbox">
                    <CheckBoxInputWithLabel
                      label={formatMessage({ id: 'dont.use' })}
                      inputProps={{
                        value: disableTime,
                        onChange: onChangeDisabledTime,
                      }}
                    />
                  </div>
                </div>
                <div className="dtp-time-input">
                  <TextInput
                    {...props}
                    disabled={disableTime}
                    className={classNames('data-picker-text', { 'is-invalid': error && touched })}
                    inputProps={{
                      value: timeValue,
                      onFocus: handleShowTimeSelector,
                      onChange: handleChangeTime,
                      onBlur: handleBlurTime,
                      onKeyDown,
                    }}
                    placeholder={currentValue ? moment.tz(currentValue, tz).format(selectedTimeFormat) : ''}
                    withLabel={withLabel}
                    inputAfter={(
                      <div className="search-wrapper">
                        <Icons customIcon className="search" width={18} height={20} icon="clock" />
                      </div>
                    )}
                    variant={variant}
                  />
                </div>
              </div>
            ) : null}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
}
